import { useMemo } from 'react'
import type { LookupUser, PaginatedResponse } from '@/domain/types'
import { isGateAgentUser } from '@/domain/gateAgent'
import { useApiQuery, type QueryState } from '@/api/useApiQuery'

/**
 * Fetches GET /api/lookup/users — the paginated list of selectable owners.
 *
 * The API caps `pageSize` at 100 (see `shared/http/pagination.ts`), and
 * 100 is plenty of headroom at Phase 2 volumes (a few dozen users). If
 * the owner pool grows past ~100, switch the picker to typeahead-with-
 * server-search rather than bumping the cap.
 *
 * Excludes the system user (`EntraUserId = 1`) at the query layer — see
 * docs/api-contracts.md — and the Gate 0 agent here, because every caller of
 * this hook is asking "which person", and the agent is not one. It keeps its
 * name everywhere it signed something; see `domain/gateAgent.ts`.
 */
export function useLookupUsers(
  page = 1,
  pageSize = 100,
): QueryState<PaginatedResponse<LookupUser>> {
  const query = useApiQuery<PaginatedResponse<LookupUser>>(
    `/api/lookup/users?page=${page}&pageSize=${pageSize}`,
  )

  return useMemo(() => {
    if (query.status !== 'success') return query
    const data = query.data.data.filter(user => !isGateAgentUser(user))
    if (data.length === query.data.data.length) return query
    // `total` is the server's count of the whole pool, so it loses the same
    // row the page did — a "1 of 24" printed against 23 rows is a bug report
    // waiting to be filed.
    return {
      status: 'success',
      data: { ...query.data, data, total: Math.max(0, query.data.total - 1) },
    }
  }, [query])
}
